import Search from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { format } from "date-fns";
import { useMemo } from "react";
import { PiutangParams } from "../../constants/types";
import { useTransaksi } from "../../context/transaksiContext";
import useDebounceFn from "../../hooks/useDebounceFn";
import usePiutang from "../../services/queries/usePiutang";
import PiutangCard from "./PiutangCard";

type Props = {
    params: PiutangParams;
    setParams: React.Dispatch<React.SetStateAction<PiutangParams>>;
};

export default function DaftarPiutang({ params, setParams }: Props) {
    const debounceSetParams = useDebounceFn(setParams);
    const query = usePiutang(params);
    const { selectedDebt, setSelectedDebt } = useTransaksi();

    const someDue = useMemo(() => {
        if (!query.data) return false;
        
        const todayStr = format(new Date(), "yyyy-MM-dd");
        const debts = query.data.content;
        
        // Cek apakah ada piutang yang:
        // 1. Belum lunas (sisapiutang > 0)
        // 2. Jatuh tempo <= hari ini
        const hasDueDebts = debts.some(debt => {
            if (debt.sisapiutang <= 0) return false;
            if (!debt.jatuhTempo) return false;
            
            const dueDateStr = format(new Date(debt.jatuhTempo), "yyyy-MM-dd");
            return dueDateStr <= todayStr;
        });
        
        return hasDueDebts;
    }, [query.data]);

    return (
        <>
            <Grid container spacing={2} p={2}>
                <Grid item xs={8}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Cari nomor invoice, nama pelanggan"
                        InputProps={{ endAdornment: <Search /> }}
                        onChange={(e) => {
                            debounceSetParams((prev) => ({
                                ...prev,
                                search: e.target.value,
                            }));
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Select
                        size="small"
                        fullWidth
                        placeholder="Urutkan"
                        defaultValue={2}
                        onChange={(e) => {
                            setParams((prev) => ({
                                ...prev,
                                urutan: e.target.value as number,
                            }));
                        }}
                    >
                        <MenuItem value={1}>Jatuh Tempo: Terakhir</MenuItem>
                        <MenuItem value={2}>Jatuh Tempo: Terawal</MenuItem>
                        <MenuItem value={3}>
                            Pembayaran Terakhir: Terbaru
                        </MenuItem>
                        <MenuItem value={4}>
                            Pembayaran Terakhir: Terlama
                        </MenuItem>
                    </Select>
                </Grid>
            </Grid>
            <Box 
                // height="calc(100vh - 21.75rem)" 
                overflow={"auto"}
            >
                {query.isLoading ? (
                    <Stack alignItems="center">
                        <CircularProgress />
                    </Stack>
                ) : query.isError ? (
                    <Alert severity="error">Terjadi kesalahan.</Alert>
                ) : (
                    <>
                        {someDue && (
                            <Box px={2} pb={2}>
                                <Alert severity="error">
                                    Ada piutang yang jatuh tempo.
                                </Alert>
                            </Box>
                        )}
                        <RadioGroup
                            value={selectedDebt}
                            onChange={(e) => {
                                setSelectedDebt(+e.target.value);
                            }}
                            sx={{
                                overflowY: "auto",
                                flexWrap: "nowrap",
                                gap: 2,
                                px: 2,
                                pb: 2,
                            }}
                        >
                            {query.data?.content.map((debt) => (
                                <PiutangCard
                                    key={debt.nomorInvoice}
                                    id={debt.id}
                                    invoiceNumber={debt.nomorInvoice}
                                    customerName={debt.pelangganNama ?? ""}
                                    remainingDebt={debt.sisapiutang}
                                    dueDate={debt.jatuhTempo}
                                    recentPayment={
                                        debt.pembayaranPiutangTerakhir
                                    }
                                    selected={selectedDebt === debt.id}
                                />
                            ))}
                        </RadioGroup>
                    </>
                )}
            </Box>
        </>
    );
}
